import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { GetCirclebyID } from "../../../Apis/organizationApi";
import {
  getAllActionCategories,
  getAllComplaintZones,
} from "../../../Apis/complaintApi";
import { useSlaExceptionRuleStore } from "./store/useSlaExceptionRuleStore";
import {
  DEFAULT_REPORT_ZONE_ID,
  DEFAULT_REPORT_ZONE_NAME,
} from "../SlaUserMatrix/store/useSlaUserMatrixStore";

bouncy.register();

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionHeaderStyle = {
  backgroundColor: "rgb(235, 240, 247)",
  color: "#003366",
  padding: "8px 12px",
  borderRadius: "4px",
  fontWeight: 600,
  marginBottom: "16px",
};

const parseDateForInput = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.split("T")[0];
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }
  return "";
};

const toCircleList = (response) =>
  Array.isArray(response) ? response : response?.data ?? [];

const AddEditSlaExceptionRuleForm = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  onSaved,
}) => {
  const { isSaving, saveSlaExceptionRule } = useSlaExceptionRuleStore();
  const [zones, setZones] = useState([]);
  const [circles, setCircles] = useState([]);
  const [actionCategories, setActionCategories] = useState([]);
  const [resolvedDefaultZoneId, setResolvedDefaultZoneId] = useState(
    DEFAULT_REPORT_ZONE_ID
  );

  const loadCirclesByZoneId = useCallback(async (zoneId) => {
    if (!zoneId) {
      setCircles([]);
      return [];
    }

    try {
      const response = await GetCirclebyID(zoneId);
      const activeCircles = toCircleList(response).filter(
        (circle) => circle.isActive !== false
      );
      setCircles(activeCircles);
      return activeCircles;
    } catch (error) {
      console.error("Error fetching wards by zone:", error);
      setCircles([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!showModal) return;

    const loadDropdownData = async () => {
      try {
        const [zoneResponse, categoryResponse] = await Promise.all([
          getAllComplaintZones(),
          getAllActionCategories(),
        ]);

        const activeZones = zoneResponse.filter((zone) => zone.isActive);
        setZones(activeZones);
        setActionCategories(categoryResponse);

        const defaultZone =
          activeZones.find((zone) => zone.zoneName === DEFAULT_REPORT_ZONE_NAME) ||
          activeZones.find(
            (zone) => String(zone.zoneId) === DEFAULT_REPORT_ZONE_ID
          );
        const zoneId = String(defaultZone?.zoneId ?? DEFAULT_REPORT_ZONE_ID);
        setResolvedDefaultZoneId(zoneId);

        if (!isEditMode) {
          await loadCirclesByZoneId(zoneId);
        }
      } catch (error) {
        console.error("Error fetching SLA exception dropdown data:", error);
        setZones([]);
        setActionCategories([]);
      }
    };

    loadDropdownData();
  }, [showModal, isEditMode, loadCirclesByZoneId]);

  useEffect(() => {
    if (!showModal || !isEditMode || !editRow) return;

    const loadEditWards = async () => {
      let zoneId = editRow?.zoneId?.toString();

      if (!zoneId && editRow?.zoneName && zones.length) {
        const matchedZone = zones.find(
          (zone) => zone.zoneName?.trim() === editRow.zoneName?.trim()
        );
        zoneId = matchedZone?.zoneId?.toString();
      }

      if (zoneId) {
        await loadCirclesByZoneId(zoneId);
      }
    };

    loadEditWards();
  }, [showModal, isEditMode, editRow, zones, loadCirclesByZoneId]);

  const handleClose = () => {
    setShowModal(false);
    setZones([]);
    setCircles([]);
    setActionCategories([]);
  };

  const resolveZoneId = () => {
    if (editRow?.zoneId) return editRow.zoneId.toString();
    const matchedZone = zones.find(
      (zone) => zone.zoneName?.trim() === editRow?.zoneName?.trim()
    );
    return matchedZone?.zoneId?.toString() || "";
  };

  const resolveWardId = () => {
    if (editRow?.wardId) return editRow.wardId.toString();
    if (editRow?.circleId) return editRow.circleId.toString();
    const wardLabel = editRow?.wardName || editRow?.wardNo;
    const matchedCircle = circles.find(
      (circle) => circle.circleName?.trim() === wardLabel?.trim()
    );
    return matchedCircle?.circleId?.toString() || "";
  };

  const resolveActionCategory = () => {
    const value = editRow?.actionCategory;
    if (value == null || value === "") return "";

    const stringValue = String(value).trim();
    const matchedCategory = actionCategories.find(
      (category) =>
        String(category.actionCategoryId) === stringValue ||
        category.actionCategoryName?.trim() === stringValue
    );

    return matchedCategory
      ? String(matchedCategory.actionCategoryId)
      : stringValue;
  };

  const initialValues = {
    zoneId: isEditMode
      ? resolveZoneId()
      : resolvedDefaultZoneId || DEFAULT_REPORT_ZONE_ID,
    wardId: isEditMode ? resolveWardId() : "",
    actionCategory: isEditMode ? resolveActionCategory() : "",
    validFrom: isEditMode ? parseDateForInput(editRow?.validFrom) : "",
    validTill: isEditMode
      ? parseDateForInput(editRow?.validTill || editRow?.validTo)
      : "",
    remarks: isEditMode ? editRow?.remarks || "" : "",
    isActive: isEditMode
      ? editRow?.isActive === false
        ? "false"
        : "true"
      : "true",
  };

  const validationSchema = Yup.object().shape({
    zoneId: Yup.string().required("Zone is required"),
    wardId: Yup.string().required("Ward is required"),
    actionCategory: Yup.string().required("Action Category is required"),
    validFrom: Yup.string().required("Valid From is required"),
    validTill: Yup.string()
      .required("Valid To is required")
      .test(
        "valid-till-after-from",
        "Valid To must be on or after Valid From",
        function (value) {
          const { validFrom } = this.parent;
          if (!value || !validFrom) return true;
          return new Date(value) >= new Date(validFrom);
        }
      ),
    remarks: Yup.string()
      .trim()
      .required("Remarks is required")
      .min(10, "Remarks must be at least 10 characters")
      .max(500, "Maximum 500 characters allowed"),
    isActive: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values) => {
    const payload = {
      zoneId: values.zoneId,
      wardId: values.wardId,
      actionCategory: values.actionCategory,
      validFrom: values.validFrom,
      validTill: values.validTill,
      remarks: values.remarks.trim(),
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      payload.slaExceptionId =
        editRow?.slaExceptionId ?? editRow?.slaExceptionRuleId;
    }

    const result = await saveSlaExceptionRule(payload, isEditMode);

    if (result?.success) {
      await onSaved?.();
      handleClose();
    }
  };

  return (
    <Modal
      size="md"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="sla-exception-rule-modal-title"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title id="sla-exception-rule-modal-title">
          {isEditMode ? "Edit SLA Exception Rule" : "Add SLA Exception Rule"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleClose();
            }
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr className="mt-4" />
      <Modal.Body>
        <Formik
          key={
            isEditMode
              ? `${editRow?.slaExceptionRuleId ?? editRow?.slaExceptionId}-${zones.length}-${circles.length}-${actionCategories.length}`
              : `new-${resolvedDefaultZoneId}-${zones.length}-${circles.length}`
          }
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <h6 className="mb-0" style={sectionHeaderStyle}>
                SLA Exception Details
              </h6>

              <div className="mb-3">
                <label htmlFor="zoneId" className="form-label">
                  Zone
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  as="select"
                  name="zoneId"
                  id="zoneId"
                  className="form-select"
                  onChange={(event) => {
                    const zoneId = event.target.value;
                    setFieldValue("zoneId", zoneId);
                    setFieldValue("wardId", "");
                    loadCirclesByZoneId(zoneId);
                  }}
                >
                  {zones.length > 0 ? (
                    zones.map((zone) => (
                      <option key={zone.zoneId} value={String(zone.zoneId)}>
                        {zone.zoneName}
                      </option>
                    ))
                  ) : (
                    <option value={DEFAULT_REPORT_ZONE_ID}>
                      {DEFAULT_REPORT_ZONE_NAME}
                    </option>
                  )}
                </Field>
                <ErrorMessage
                  className="required-message"
                  name="zoneId"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="wardId" className="form-label">
                  Ward
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Select
                  inputId="wardId"
                  name="wardId"
                  classNamePrefix="react-select"
                  options={circles.map((circle) => ({
                    value: String(circle.circleId),
                    label: circle.circleName,
                  }))}
                  value={
                    circles
                      .map((circle) => ({
                        value: String(circle.circleId),
                        label: circle.circleName,
                      }))
                      .find((option) => option.value === String(values.wardId)) ||
                    null
                  }
                  onChange={(selected) =>
                    setFieldValue("wardId", selected ? selected.value : "")
                  }
                  placeholder="Select Ward"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage
                  className="required-message"
                  name="wardId"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="actionCategory" className="form-label">
                  Action Category
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Select
                  inputId="actionCategory"
                  name="actionCategory"
                  classNamePrefix="react-select"
                  options={actionCategories.map((category) => ({
                    value: String(category.actionCategoryId),
                    label: category.actionCategoryName,
                  }))}
                  value={
                    actionCategories
                      .map((category) => ({
                        value: String(category.actionCategoryId),
                        label: category.actionCategoryName,
                      }))
                      .find(
                        (option) =>
                          option.value === String(values.actionCategory)
                      ) || null
                  }
                  onChange={(selected) =>
                    setFieldValue("actionCategory", selected ? selected.value : "")
                  }
                  placeholder="Select Action Category"
                  isClearable={false}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
                <ErrorMessage
                  className="required-message"
                  name="actionCategory"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="validFrom" className="form-label">
                  Valid From
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="date"
                  name="validFrom"
                  id="validFrom"
                  className="form-control"
                />
                <ErrorMessage
                  className="required-message"
                  name="validFrom"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="validTill" className="form-label">
                  Valid To
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  type="date"
                  name="validTill"
                  id="validTill"
                  className="form-control"
                />
                <ErrorMessage
                  className="required-message"
                  name="validTill"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="remarks" className="form-label">
                  Remarks
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <Field
                  as="textarea"
                  rows={3}
                  name="remarks"
                  id="remarks"
                  placeholder="Enter Exception Remarks"
                  className="form-control"
                />
                <ErrorMessage
                  className="required-message"
                  name="remarks"
                  component="div"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Status
                  <span style={{ color: "#EC1117" }}> *</span>
                </label>
                <div className="d-flex align-items-center gap-4 mt-1">
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaExceptionActive"
                      value="true"
                    />
                    <label className="form-check-label" htmlFor="slaExceptionActive">
                      Active
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="slaExceptionInactive"
                      value="false"
                    />
                    <label className="form-check-label" htmlFor="slaExceptionInactive">
                      Inactive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  className="required-message"
                  name="isActive"
                  component="div"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button type="button" style={cancelButtonStyle} onClick={handleClose}>
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditSlaExceptionRuleForm;
